import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column, DataTable, getDefaultSorting, YColumnsType } from '@ydistri/ds';
import { EntityListImportAction, EntityListImportItemResponse } from '@ydistri/api-sdk';
import { useColumnWidth } from '../../../../../hooks/useColumnWidth';
import {
  GetEntityListImportItemsRequest,
  useGetEntityListImportItemsQuery,
} from '../../apiEntityLists';
import {
  EntityListContentItem,
  EntityListTypeSupported,
  getEntityListSpecification,
} from '../../entityListsLib';
import {
  EntityColumns,
  EntityColumnsConfig,
  useProductTableColumnsProvider,
  useStoreTableColumnsProvider,
} from '../../../../../hooks/useEntityTableColumnsProvider';
import { useCurrentEntityListState } from '../../hooks/useCurrentEntityListState';
import ScrolledTableWrapper from '../../../../../components/global/ScrolledTableWrapper/ScrolledTableWrapper';
import LoadingIndicator from '../../../../../components/global/LoadingIndicator/LoadingIndicator';

interface EntityListItemImportGridProps {
  entityListType: EntityListTypeSupported;
  selectedEntityListId: number;
}

const BLOCK_SIZE = 45;

const EntityListItemImportGrid: React.FC<EntityListItemImportGridProps> = ({
  entityListType,
  selectedEntityListId,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { productIdColumnWidth, storeIdColumnWidth } = useColumnWidth();
  const [tableHeight, setTableHeight] = useState(320);

  const entityListState = useCurrentEntityListState(entityListType);
  const selectedValidationImportAction = entityListState?.selectedValidationImportAction;

  const storeColumnsProvider = useStoreTableColumnsProvider();
  const productColumnsProvider = useProductTableColumnsProvider();

  const columns: YColumnsType<EntityListImportItemResponse>[] = useMemo(() => {
    const tmpColumns: YColumnsType<EntityListImportItemResponse>[] = [];
    const entityListImpl = getEntityListSpecification(entityListType);

    if (entityListImpl.getContentTableStructure().includes(EntityListContentItem.PRODUCT)) {
      const productColumnsConfig: EntityColumnsConfig<EntityListImportItemResponse> = {
        [EntityColumns.ID]: {
          width: productIdColumnWidth,
          apiFilterable: true,
          render: (value: string, record) =>
            !value || value === '' ? record.productIdentification : value,
        },
        [EntityColumns.NAME]: {
          width: '29rem',
          apiFilterable: true,
        },
        [EntityColumns.CODE]: {
          width: '6rem',
          apiFilterable: true,
        },
      };
      const productColumns =
        productColumnsProvider<EntityListImportItemResponse>(productColumnsConfig);

      tmpColumns.push(...productColumns);
    }

    if (entityListImpl.getContentTableStructure().includes(EntityListContentItem.STORE)) {
      const storeColumnsConfig: EntityColumnsConfig<EntityListImportItemResponse> = {
        [EntityColumns.ID]: {
          width: storeIdColumnWidth,
          apiFilterable: true,
          render: (value: string, record) =>
            !value || value === '' ? record.storeIdentification : value,
        },
        [EntityColumns.NAME]: {
          width: '29rem',
          apiFilterable: true,
        },
        [EntityColumns.CODE]: {
          width: '10rem',
          apiFilterable: true,
        },
      };
      const storeColumns = storeColumnsProvider<EntityListImportItemResponse>(storeColumnsConfig);
      tmpColumns.push(...storeColumns);

      if (entityListImpl.getContentTableStructure().includes(EntityListContentItem.QUANTITY)) {
        tmpColumns.push({
          title: 'Quantity',
          key: 'quantity',
          dataIndex: 'quantity',
          width: '10rem',
          align: 'right',
          apiColumnName: 'Quantity',
          apiFilterable: true,
          apiFilterType: 'number',
          sorter: true,
        });
      }
    }

    return tmpColumns;
  }, [
    entityListType,
    productColumnsProvider,
    productIdColumnWidth,
    storeColumnsProvider,
    storeIdColumnWidth,
  ]);

  const params: GetEntityListImportItemsRequest = useMemo(() => {
    return {
      entityListId: selectedEntityListId,
      entityListImportAction: selectedValidationImportAction ?? EntityListImportAction.Insert,
      skip: 0,
      top: BLOCK_SIZE,
      sortings: getDefaultSorting(columns),
    };
  }, [selectedValidationImportAction, columns, selectedEntityListId]);

  const { data, isFetching, refetch } = useGetEntityListImportItemsQuery(params);

  const resizeTable = useCallback(() => {
    if (ref.current) {
      setTableHeight(ref.current.clientHeight);
    }
  }, []);

  useEffect(() => {
    resizeTable();

    //compute height on window resize
    window.addEventListener('resize', resizeTable);

    return () => {
      window.removeEventListener('resize', resizeTable);
    };
  }, [refetch, resizeTable]);

  const rowKeyProvider = useCallback(
    (row: EntityListImportItemResponse) =>
      row.sku?.skuId
        ? `sku-${row.sku.skuId}`
        : `s-${row.store?.id ?? row.storeIdentification}-p-${row.product?.id ?? row.productIdentification}`,
    [],
  );

  if (!isFetching && data) {
    return (
      <ScrolledTableWrapper offset={40} minHeight={tableHeight}>
        <DataTable<EntityListImportItemResponse>
          id="entityListImportItemsContent"
          height={tableHeight}
          columns={columns}
          dataSource={data}
          loading={isFetching}
          rowKey={rowKeyProvider}
          showHeader={true}
          virtual
          scroll={{ y: tableHeight, x: 600 }}
          flexGrow={1}
        />
      </ScrolledTableWrapper>
    );
  } else {
    return (
      <Column $flexGrow={1}>
        <LoadingIndicator />
      </Column>
    );
  }
};

export default EntityListItemImportGrid;
